import { Button, UpgradeSubscriptionModalType, ViewerContext } from "dash-ui";
import { useTranslation } from "react-i18next";
import styled from "styled-components";

import { IActionButtonsProps } from "./ActionButtons.interfaces";
import { useCallback, useContext } from "react";
import { RecurringTypesEnum } from "dash-common";

const ButtonStyle = styled(Button)<{ isLight: boolean; colorVariation }>`
  background-color: ${({ colorVariation, isLight, theme }) =>
    colorVariation === "danger"
      ? "red"
      : isLight
      ? theme.colors.b100
      : theme.colors.white};
`;

const ActionButtons = ({
  currentBrokerSubscription,
  subscription,
  isLight,
  onSubscriptionProductSelect,
  onSubscriptionProductCancel,
  recurringType,
  selectButtonLoading,
  cancelButtonLoading,
  type,
  onClose,
}: IActionButtonsProps) => {
  const { t } = useTranslation(["pages", "common"]);
  const { trial } = subscription;

  const subscriptionPrice =
    recurringType === RecurringTypesEnum.SIXMONTH
      ? subscription.price / 6
      : subscription.price;

  const brokerPrice =
    currentBrokerSubscription?.recurringType === RecurringTypesEnum.SIXMONTH
      ? (currentBrokerSubscription?.price ?? 0) / 6
      : currentBrokerSubscription?.price;

  const isDowngrade = subscriptionPrice < (brokerPrice || 0);
  const isSamePrice = subscriptionPrice === brokerPrice;
  const isSameProduct = subscription.name === currentBrokerSubscription?.name;

  const getLabelKey = () => {
    if (isSameProduct) {
      return isSamePrice
        ? "pages:completeSignUpPickSubscription:stayOnthisPlan"
        : subscription.price < (currentBrokerSubscription?.price || 0)
        ? "pages:profilePage.billing.changePlanConfirmationModal.changeToMonthlyPlan"
        : "pages:profilePage.billing.changePlanConfirmationModal.changeToSixMonthPlan";
    }

    if (!currentBrokerSubscription) {
      return `pages:completeSignUpPickSubscription:${
        subscription.name === "Basic"
          ? "subscribeBasic"
          : subscription.name === "PRO"
          ? "subscribePro"
          : "subscribeIdx"
      }`;
    }

    return isSamePrice
      ? "pages:completeSignUpPickSubscription:stayOnthisPlan"
      : `pages:profilePage.billing.changePlanConfirmationModal.${
          isDowngrade ? "downgrade" : "upgrade"
        }Title`;
  };

  const buttonLabel = t(getLabelKey(), { name: subscription.name });

  const onClick =
    isDowngrade && !isSameProduct
      ? onSubscriptionProductCancel
      : onSubscriptionProductSelect;
  const loading = isLight ? selectButtonLoading : cancelButtonLoading;

  const ActionButton = useCallback(
    ({
      label,
      variation,
      colorVariation,
      onClose,
    }: {
      label: string;
      variation?: "primary" | "outline" | "flat";
      colorVariation?: "default" | "danger" | "success" | "light" | undefined;
      onClose?: () => void;
    }) => (
      <ButtonStyle
        mt={4}
        onClick={() => {
          if (onClose) {
            onClose && onClose();
            return;
          }
          onClick && onClick(subscription._id);
        }}
        w="100%"
        isLight={isLight}
        variation={variation}
        colorVariation={colorVariation}
        isLoading={loading}>
        {label}
      </ButtonStyle>
    ),
    [isLight, subscription._id, loading]
  );

  const FlatConfirmButton = useCallback(
    ({ label, onClose }: { label: string; onClose?: () => void }) => (
      <ActionButton onClose={onClose} label={label} variation="flat" />
    ),
    []
  );

  const PrimaryConfirmButton = useCallback(
    ({ label, onClose }: { label: string; onClose?: () => void }) => (
      <ActionButton onClose={onClose} label={label} variation="primary" />
    ),
    []
  );

  const getButton = useCallback(
    ({
      trial,
      type,
      isLight,
    }: {
      trial?: number;
      type: UpgradeSubscriptionModalType;
      isLight: boolean;
    }) => {
      switch (type) {
        case UpgradeSubscriptionModalType.BASIC:
        case UpgradeSubscriptionModalType.DOWNLOAD:
        case UpgradeSubscriptionModalType.PORTFOLIO:
        case UpgradeSubscriptionModalType.GENERAL:
        case UpgradeSubscriptionModalType.PICK_PLAN:
        case UpgradeSubscriptionModalType.IDX:
        case UpgradeSubscriptionModalType.PRO: {
          return isLight ? (
            <PrimaryConfirmButton
              onClose={isSamePrice ? onClose : undefined}
              label={buttonLabel}
            />
          ) : (
            <FlatConfirmButton
              onClose={isSamePrice ? onClose : undefined}
              label={buttonLabel}
            />
          );
        }
      }
    },
    []
  );

  return <>{getButton({ trial, type, isLight })}</>;
};

export default ActionButtons;
